import React, { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useLocation } from "react-router-dom";
import { X, MessageSquare } from "lucide-react";
import { track } from "../../lib/analytics";
import { CONTACT_EMAIL } from "../../lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", email: "", phone: "", company: "", problem: "" };

/* Persistent "Talk to Us" trigger, desktop corner pill + mobile bottom bar.
   Opens an in-page side drawer (no navigation) that captures a lead + page context. */
export const TalkToUs = ({ open, onOpenChange, context }) => {
  const { pathname } = useLocation();
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState("idle"); // idle | loading | done | error

  useEffect(() => {
    if (open) {
      track("talk_to_us_opened", { source_page: pathname, seed: context?.query ? "find_your_switch" : "direct" });
      setForm({ ...empty, problem: context?.query || "" });
      setStatus("idle");
    }
  }, [open, context, pathname]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.problem.trim()) return;
    setStatus("loading");
    track("talk_to_us_submitted", { source_page: pathname, situation: context?.situation || null });
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          company: form.company,
          message: form.problem,
          source_page: pathname,
          service: context?.situation || "",
          referrer: typeof document !== "undefined" ? document.referrer : "",
        }),
      });
      if (!res.ok) throw new Error("bad status");
      setStatus("done");
    } catch (err) {
      setStatus("error");
    }
  };

  const field =
    "font-body w-full border-2 border-black/15 bg-white px-4 py-3.5 text-[18px] leading-normal text-black outline-none transition-colors placeholder:text-black/40 focus:border-[#5f8f10]";
  const labelCls = "font-body mb-2 block text-sm font-semibold uppercase tracking-[0.12em] text-black";

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[110] bg-black/55 backdrop-blur-[3px] data-[state=open]:animate-in data-[state=open]:fade-in data-[state=closed]:animate-out data-[state=closed]:fade-out motion-reduce:animate-none" />
        <Dialog.Content
          data-testid="talk-drawer"
          className="fixed inset-y-0 right-0 z-[120] flex w-full max-w-[520px] flex-col overflow-y-auto bg-white shadow-2xl outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right data-[state=open]:duration-300 motion-reduce:animate-none"
        >
          {/* Header */}
          <div className="flex items-start justify-between border-b-2 border-black bg-black px-7 py-6 text-white">
            <div>
              <p className="font-body text-sm font-semibold uppercase tracking-[0.18em] text-[#5f8f10]">Talk to us</p>
              <Dialog.Title className="font-display mt-2 text-3xl uppercase leading-[0.95] tracking-tight md:text-4xl">
                Bring us your<br />growth problem.
              </Dialog.Title>
              <Dialog.Description className="font-body mt-3 text-[18px] font-normal leading-relaxed text-white/80">
                Tell us what needs to move. We&rsquo;ll read it and come back to you.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close"
                data-testid="talk-drawer-close"
                className="ml-4 flex h-11 w-11 shrink-0 items-center justify-center border-2 border-white/25 text-white transition-colors hover:border-[#5f8f10] hover:text-[#5f8f10]"
              >
                <X size={22} strokeWidth={2} />
              </button>
            </Dialog.Close>
          </div>

          {status === "done" ? (
            <div data-testid="talk-drawer-confirm" className="flex flex-1 flex-col justify-center px-7 py-12">
              <div className="flex h-16 w-16 items-center justify-center bg-[#5f8f10]">
                <span className="font-display text-4xl leading-none text-black">✓</span>
              </div>
              <h3 className="font-display mt-7 text-3xl uppercase tracking-tight text-black md:text-4xl">Thank you.</h3>
              <p className="font-body mt-4 max-w-[42ch] text-[18px] font-normal leading-relaxed text-black/75">
                We&rsquo;ve received your growth problem. A member of the SWITCH team will review it and be in touch.
              </p>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                data-testid="talk-drawer-confirm-close"
                className="font-body mt-8 inline-flex w-fit items-center gap-3 border-2 border-black bg-black px-8 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-colors hover:bg-[#5f8f10] hover:border-[#5f8f10] hover:text-black"
              >
                Close <span aria-hidden>→</span>
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-1 flex-col gap-5 px-7 py-8">
              <div>
                <label htmlFor="talk-name" className={labelCls}>Your name</label>
                <input id="talk-name" required data-testid="talk-name" className={field} value={form.name} onChange={set("name")} placeholder="Full name" />
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="talk-email" className={labelCls}>Work email</label>
                  <input id="talk-email" required type="email" data-testid="talk-email" className={field} value={form.email} onChange={set("email")} placeholder="name@company.com" />
                </div>
                <div>
                  <label htmlFor="talk-phone" className={labelCls}>Phone</label>
                  <input id="talk-phone" type="tel" data-testid="talk-phone" className={field} value={form.phone} onChange={set("phone")} placeholder="+91 00000 00000" />
                </div>
              </div>
              <div>
                <label htmlFor="talk-company" className={labelCls}>Company <span className="font-normal normal-case tracking-normal text-black/40">(optional)</span></label>
                <input id="talk-company" data-testid="talk-company" className={field} value={form.company} onChange={set("company")} placeholder="Company · website" />
              </div>
              <div>
                <label htmlFor="talk-problem" className={labelCls}>What problem are we solving?</label>
                <textarea id="talk-problem" required rows={4} data-testid="talk-problem" className={`${field} resize-none`} value={form.problem} onChange={set("problem")} placeholder="Describe what is not moving, what has changed, or what you are trying to achieve." />
              </div>

              {status === "error" && (
                <p data-testid="talk-error" className="font-body text-base font-semibold text-[#5f8f10]">
                  Something went wrong sending that. Please try again, or write to {CONTACT_EMAIL}.
                </p>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="talk-submit"
                className="font-body mt-1 inline-flex w-full items-center justify-between gap-3 border-2 border-[#5f8f10] bg-[#5f8f10] px-8 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-black transition-colors hover:bg-black hover:border-black hover:text-white disabled:opacity-60"
              >
                {status === "loading" ? "Sending…" : "Send it to SWITCH"} <span aria-hidden>→</span>
              </button>
              <p className="font-body text-sm font-normal text-black/50">Or write to {CONTACT_EMAIL}</p>
            </form>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

/* Floating persistent trigger rendered globally. */
const openFinderEvt = () => window.dispatchEvent(new CustomEvent("switch:finder"));
export const TalkTrigger = ({ onOpen }) => (
  <>
    {/* Desktop corner pills */}
    <div className="fixed bottom-7 right-7 z-[90] hidden items-center gap-2.5 lg:flex">
      <button
        type="button"
        data-testid="finder-trigger-desktop"
        onClick={openFinderEvt}
        className="font-body inline-flex items-center gap-2 rounded-full border border-white/15 bg-[#161A15]/90 px-5 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] text-white shadow-[0_10px_30px_rgba(0,0,0,0.3)] backdrop-blur transition-colors duration-200 hover:border-[#5f8f10] hover:text-[#5f8f10]"
      >
        Find your Switch
      </button>
      <button
        type="button"
        data-testid="talk-trigger-desktop"
        onClick={onOpen}
        className="group font-body inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-[#161A15] py-3.5 pl-5 pr-6 text-sm font-semibold uppercase tracking-[0.12em] text-white shadow-[0_10px_30px_rgba(0,0,0,0.35)] transition-colors duration-200 hover:bg-[#5f8f10] hover:text-[#161A15]"
      >
        <span aria-hidden className="relative flex h-2.5 w-2.5 items-center justify-center">
          <span className="absolute inline-flex h-full w-full motion-safe:animate-ping rounded-full bg-[#5f8f10] opacity-70 group-hover:bg-[#161A15]" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#5f8f10] group-hover:bg-[#161A15]" />
        </span>
        Talk to Us
      </button>
    </div>

    {/* Mobile bottom bar: split into Find your Switch + Talk to Us */}
    <div className="fixed inset-x-0 bottom-0 z-[90] flex border-t border-white/10 lg:hidden">
      <button
        type="button"
        data-testid="finder-trigger-mobile"
        onClick={openFinderEvt}
        className="font-body flex-1 bg-[#161A15] px-4 py-4 text-sm font-semibold uppercase tracking-[0.1em] text-white active:bg-[#1c2318]"
      >
        Find your Switch
      </button>
      <button
        type="button"
        data-testid="talk-trigger-mobile"
        onClick={onOpen}
        className="font-body flex flex-1 items-center justify-center gap-2 border-l border-white/10 bg-[#5f8f10] px-4 py-4 text-sm font-semibold uppercase tracking-[0.1em] text-[#0f1408]"
      >
        <MessageSquare size={17} strokeWidth={2.25} />
        Talk to Us
      </button>
    </div>
  </>
);

export default TalkToUs;
